import type { Theme } from '@mui/material';

/**
 * Theme-aware background for read-only "detail" surfaces: the panels inside
 * dialogs that restate a record (attack type details) or dump a config schema.
 *
 * These used to be `bgcolor: 'grey.50'`, a fixed palette value that does not
 * flip with the palette mode: in dark mode it painted a near-white panel under
 * text that inherits a near-white `text.primary` (CAO-226). The values below are
 * the design system's secondary/elevated backgrounds (see
 * `.cursor/rules/frontend-designer.mdc`) and clear WCAG AA against both
 * `text.primary` and `text.secondary` in either mode.
 */
export const detailSurfaceBackground = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9';

/**
 * `sx` for a read-only detail surface — background, padding, and a divider
 * border so the panel still reads as its own surface in dark mode, where the
 * contrast against the dialog's paper is deliberately subtle.
 *
 * Usage: `<Box sx={detailSurfaceSx} />` (MUI calls it with the active theme).
 */
export const detailSurfaceSx = (theme: Theme) => ({
  p: 2,
  borderRadius: 1,
  bgcolor: detailSurfaceBackground(theme),
  border: `1px solid ${theme.palette.divider}`,
  color: 'text.primary',
});
